/**
 * @file   src\Pages\MyProfile.tsx
 * @brief  MyProfile page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, useEffect, useOutletContext, Container, Button, Row, Col, useNavigate, Tab, Tabs } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWInput from '../components/CWInput';
import CWPasswordInput from '../components/CWPasswordInput';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType } from '../interfaces/generalInterface';
import * as Yup from 'yup'
import { CHANGE_PASSWORD_SCHEMA, MYPROFILE_SCHEMA } from '../validations/myprofileSchema';
import { EMAIL_MAX_LENGTH, NAME_MAX_LENGTH, PASSWORD_MAX_LENGTH } from '../utils/constants';
import { ProfileApi } from "../utils/apiUrls";
import axios from '../config/axiosConfig';
import SmallLoader from '../components/SmallLoader';
import { MessageToaster } from "../utils/toastUtil";

const MyProfile = () => {
    // Access the context
    const {  updateData } = useOutletContext<AppContextType>();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate()
    const toaster = new MessageToaster();
    // Initialize states for formdata
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        oldpassword: '',
        newpassword: '',
        retypepassword: '',

    })

    // Handle errors
    const [errors, setErrors] = useState({
        name: '',
        email: '',
        oldpassword: '',
        newpassword: '',
        retypepassword: '',
    });


    // To display heading
    useEffect(() => {
        handleUpdate();
        fetchProfileData()
    }, [])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "My Profile", url: "", showBackButton: false, showMainMenu: true, activeLink: "manage-car-wash" });
    };

    const onInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            // Validate form data
            await MYPROFILE_SCHEMA.validate(formData, { abortEarly: false });
            try {
                setLoading(true)
                const responseLocationProfileUpdate = await axios.post(ProfileApi.PROFILE_UPDATE_API, {
                    name: formData.name,
                    email: formData.email,
                  });
                setLoading(false)
                if (responseLocationProfileUpdate?.data?.error === 0) {
                    setErrors({
                        name: '',
                        email: '',
                        oldpassword: '',
                        newpassword: '',
                        retypepassword: '',
                    });
                    
                    toaster.toastSuccess(responseLocationProfileUpdate?.data?.message);
                   
                }
                else {
                    toaster.toastError(responseLocationProfileUpdate?.data?.message);
                }
            } catch {
                setLoading(false)
                toaster.toastError('Failed to update profile.');
            }

        } catch (error) {

            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }
    }

    const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            // Validate form data
            await CHANGE_PASSWORD_SCHEMA.validate(formData, { abortEarly: false });
           
            try {
                setLoading(true)
                const responseLocationProfileUpdate = await axios.post(ProfileApi.PASSWORD_UPDATE_API, {
                    oldPassword: formData.oldpassword,
                    newPassword: formData.newpassword,
                    confirmPassword : formData.retypepassword
                  });
                setLoading(false)
                if (responseLocationProfileUpdate?.data?.error === 0) {
                    setErrors({
                        name: '',
                        email: '',
                        oldpassword: '',
                        newpassword: '',
                        retypepassword: '',
                    });
                    setFormData({
                        ...formData,
                        oldpassword: '',
                        newpassword: '',
                        retypepassword: '',
                    });
                    toaster.toastSuccess(responseLocationProfileUpdate?.data?.message);
                }
                else {
                    toaster.toastError(responseLocationProfileUpdate?.data?.message);
                }
            } catch  {
                setLoading(false)
                toaster.toastError('Failed to update password.');
            }
        } catch (error) {

            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }
    }

    const fetchProfileData = async () => {
        try {
            setLoading(true)
            const apiUrl =  ProfileApi.FETCH_PROFILE_API;
            const { data } = await axios.get(apiUrl);
            if(data?.error === 0) {
                setLoading(false)
                const name = data?.data?.fullName;
                const Email = data?.data?.email;
                setFormData({
                    ...formData,
                    ['name']: name,
                    ['email']: Email,
                });
            }
        }  catch (error: any) {
            console.error('Error fetching company information:', error.message);
        }
    }

    return (

        <div className='manage-subscription-container'>
            <Container>

                <div className='cw-form-outer-wrap'>
                    <div className="cw-main-tab">
                        <Tabs
                            defaultActiveKey="profile"
                            id="profile-tab"
                            className="cw-tab-title justify-content-end"
                        >
                            <Tab eventKey="profile" title="Profile">
                                <form onSubmit={handleSubmit}>
                                    <div className="cw-form-container">
                                        <div className="form-inner mx-auto">
                                            <h5>Personal Information</h5>
                                            <Row>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="name"
                                                        name="name"
                                                        type="text"
                                                        label="Name"
                                                        value={formData.name}
                                                        onChange={onInputChange}
                                                        maxLength={NAME_MAX_LENGTH}
                                                        autoFocus
                                                        isMandatory
                                                        errorMessage={errors.name}
                                                        autoComplete='off'

                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="email"
                                                        name="email"
                                                        type="text"
                                                        label="Email Address"
                                                        value={formData.email}
                                                        onChange={onInputChange}
                                                        maxLength={EMAIL_MAX_LENGTH}
                                                        errorMessage={errors.email}
                                                        isMandatory
                                                        autoComplete="off"

                                                    />
                                                </Col>
                                            </Row>
                                            <div className="btn-sec d-flex justify-content-end">
                                                <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.dashboard}`)}>Cancel</Button>
                                                <Button title="Save" variant="primary" className="primary-btn" type='submit'>Save</Button>
                                            </div>
                                        </div>

                                    </div>
                                </form>
                            </Tab>
                            <Tab eventKey="changepassword" title="Change Password">
                                <form onSubmit={handleChangePassword}>
                                    <div className="cw-form-container">
                                        <div className="form-inner mx-auto">
                                            <h5>Change Password</h5>
                                            <Row>
                                                <Col md={6}>
                                                    <CWPasswordInput
                                                        id="oldpassword"
                                                        name="oldpassword"
                                                        type={'password'}
                                                        label="Old Password"
                                                        showpass={<span />}
                                                        value={formData.oldpassword}
                                                        onChange={onInputChange}
                                                        maxLength={PASSWORD_MAX_LENGTH}
                                                        isMandatory
                                                        autoComplete="off"
                                                        errorMessage={errors.oldpassword}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <CWPasswordInput
                                                        id="newpassword"
                                                        name="newpassword"
                                                        type={'password'}
                                                        label="New Password"
                                                        showpass={<span />}
                                                        value={formData.newpassword}
                                                        onChange={onInputChange}
                                                        maxLength={PASSWORD_MAX_LENGTH}
                                                        isMandatory
                                                        autoComplete='off'
                                                        errorMessage={errors.newpassword}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <CWPasswordInput
                                                        id="retypepassword"
                                                        name="retypepassword"
                                                        type={'password'}
                                                        label="Re-Type Password"
                                                        showpass={<span />}
                                                        value={formData.retypepassword}
                                                        onChange={onInputChange}
                                                        maxLength={PASSWORD_MAX_LENGTH}
                                                        isMandatory
                                                        autoComplete='off'
                                                        errorMessage={errors.retypepassword}
                                                    />
                                                </Col>
                                            </Row>
                                            <div className="btn-sec d-flex justify-content-end">
                                                <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.dashboard}`)}>Cancel</Button>
                                                <Button title="Save" variant="primary" className="primary-btn" type='submit'>Save</Button>
                                            </div>
                                        </div>

                                    </div>
                                </form>
                            </Tab>
                        </Tabs>
                    </div>
                </div>

            </Container>
            {loading && <SmallLoader />}
        </div>
    )
}

export default MyProfile;
